import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@y42/environment';
import escapeRegExp from 'lodash/escapeRegExp';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { UnauthorizedService } from './unauthorized.service';

@Injectable()
export class AccessTokenInterceptor implements HttpInterceptor {
  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly unauthorizedService: UnauthorizedService,
  ) {}

  /**
   * URLs which the y42-auth-token should not be added to
   */
  private readonly whitelistUrls: RegExp[] = [
    /https:\/\/api\.(dev|prod)\.y42\.dev\/(api|v2)/i,
    /\.y42\..*\/(api|v2)\//i, // match any Y42 /api/ endpoint
  ];

  private readonly ignore403Urls: RegExp[] = [
    // Ingore all ACL endpoints
    new RegExp(escapeRegExp(environment.url.acl_api)),
    //
    // TODO: Gabi, remove below logic as its supposedly not necessary anymore
    // https://github.com/goes-funky/y42-frontend/pull/3515#pullrequestreview-1389445341
    //
    // Ignore org-access-token endpoints
    new RegExp(`${escapeRegExp(environment.url.identity_api)}/keys`),
    new RegExp(
      `${escapeRegExp(
        environment.url.company_api,
      )}/orgs/\\d+/linked-accounts/[0-9a-fA-F]+(-[0-9a-fA-F]+){4}/hooks/test`,
    ),
  ];

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.whitelistUrls.find((whitelist) => whitelist.test(req.url))) {
      return next.handle(req);
    }

    return of(req).pipe(
      switchMap(() => this.authService.selectToken().pipe(take(1))),
      map((token) => this.setAuthorizationHeader(req, token)),
      switchMap((request) => {
        return next.handle(request).pipe(
          catchError((error: unknown) => {
            if (this.ignore403Urls.find((url) => url.test(request.url))) {
              return throwError(() => error);
            }

            if (error instanceof HttpErrorResponse) {
              if (isNotAuthenticated(error)) {
                console.debug(
                  `[AccessTokenInterceptor]: requesting "${request.urlWithParams}" resulted in ${error.status}, refreshing token`,
                );

                // getting here should now be practically impossible
                // due to `authService.selectToken` guaranteeing the freshness of a token
                return this.retryRequest(request, next);
              } else if (isNotAuthorized(error)) {
                if (error.error?.message?.match(/two factor/)) {
                  this.router.navigateByUrl('/two-factor');
                } else {
                  this.unauthorizedService.navigateToUnauthorizedRoute(request.url);
                }
              }
            }

            return throwError(() => error);
          }),
        );
      }),
    );
  }

  private retryRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.authService.selectToken().pipe(
      take(1),
      switchMap((token) => {
        request = this.setAuthorizationHeader(request, token);

        return next.handle(request).pipe(
          catchError(async (error: unknown) => {
            if (isNotAuthenticated(error as HttpErrorResponse)) {
              await this.authService.signIn({ saveCurrentUrl: true });

              // Non-reachable code
              throw new Error('[AccessTokenInterceptor]: should have been redirected to the login page');
            }

            throw error;
          }),
        );
      }),
    );
  }

  private setAuthorizationHeader(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
}

const isNotAuthorized = (error: HttpErrorResponse) => error instanceof HttpErrorResponse && error.status === 403;
const isNotAuthenticated = (error: HttpErrorResponse) => error instanceof HttpErrorResponse && error.status === 401;
